import {
  type CreatorListItem,
  type CreatorListItemServer,
  type CreatorsFilter,
  type CreatorsFilterServer,
  CreatorStatusEnum,
  DefaultIcon,
  getSocialNetwork,
  makeQueryString,
  sanitizeSocialSlug,
  type SocialItem,
  type SocialItemServer,
  type UserTag,
  type UserTagServer,
} from '@metapic-apps/shared'
import { markRaw } from 'vue'

import { normalizeClientListItem } from '@/lib/adapters/clients.adapter'
import { normalizeRevenueTier } from '@/lib/adapters/common.adapter'
import { normalizeImage } from '@/lib/adapters/image.adapters'

export const normalizeSocialItem = (social: SocialItemServer): SocialItem => {
  const data = getSocialNetwork(social.type)
  const item = {
    id: social.id,
    title: 'Website',
    link: sanitizeSocialSlug(social.type, social.identifier),
    identifier: social.identifier,
    followers: social.followers,
    icon: DefaultIcon,
    isConnected: social.state === 'connected',
    createdAt: social.created_at,
    updatedAt: social.updated_at,
    type: social.type,
    isPrimary: social.is_primary,
    profileId: social.profile_id,
  }

  if (data) {
    item.title = data.title
    item.icon = data.icon
  }

  return markRaw(item)
}

export const normalizeCreatorStatus = (data: CreatorListItemServer): CreatorStatusEnum => {
  if (data.is_suspended) {
    return CreatorStatusEnum.Banned
  }

  if (data.is_verified) {
    return CreatorStatusEnum.Approved
  }

  return CreatorStatusEnum.NotApproved
}

export const normalizeUserTags = (data: UserTagServer[]): UserTag[] => {
  return data.map((item) => ({ id: item.id, name: item.name, slug: item.slug }))
}

export const normalizeCreatorListItem = (data: CreatorListItemServer): CreatorListItem => {
  return {
    id: data.id,
    username: data.username,
    email: data.email,
    firstName: data.first_name,
    lastName: data.surname,
    client: normalizeClientListItem(data.client),
    revenueTier: normalizeRevenueTier(data.revenue_tier),
    latestPaymentDate: data.latest_payment_date,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    socials: data.social_media.map(normalizeSocialItem),
    isSuspended: data.is_suspended,
    isVerified: data.is_verified,
    status: normalizeCreatorStatus(data),
    ...(data.user_image && { avatar: normalizeImage(data.user_image) }),
    tags: data.user_tags.length ? normalizeUserTags(data.user_tags) : [],
  }
}

export const normalizeCreators = (data: CreatorListItemServer[]): CreatorListItem[] => {
  return data.map(normalizeCreatorListItem)
}

export const handleFilter = (filter: CreatorsFilter): CreatorsFilterServer => {
  const { client: _client, ...rest } = filter
  const result = { ...rest } as CreatorsFilterServer
  delete result.advertiserGroup
  delete result.status

  if (filter.advertiserGroup) {
    result.storeGroup = filter.advertiserGroup.id
  }

  if (filter.client) {
    result.legacyClientId = filter.client.id
  }

  if (filter.status) {
    result.userStatus = filter.status
  }

  return result
}

export const makeQueryStringForCreators = (filter: CreatorsFilter): string | null => {
  const updatedFilter = handleFilter(filter)

  return makeQueryString(updatedFilter)
}

export const makeQueryParamsForCreators = (filter: CreatorsFilter): URLSearchParams | undefined => {
  const queryString = makeQueryStringForCreators(filter)

  return queryString ? new URLSearchParams(queryString) : undefined
}
